import type { ReactNode } from 'react';
import { useRBAC } from '../../contexts/RBACContext';
import { LoadingSpinner } from './LoadingSpinner';

interface RequirePermissionProps {
  /** Permission the user must hold to see `children`. A list means ANY of them. */
  permission: string | readonly string[];
  children: ReactNode;
  /** Rendered when the user lacks the permission (defaults to a "no access" note). */
  fallback?: ReactNode;
  /**
   * Ignore the global audit-only flag and require the permission for real.
   *
   * Without this the guard defers to `hasPermission`, which grants EVERYTHING
   * while `RBAC_ENFORCEMENT_ENABLED` is false — the state prod is in today. That
   * is the right default for a page waiting on the org-wide rollout, and exactly
   * wrong for one whose proxy routes already use `alwaysEnforce`: the UI would
   * render for a colleague the API then answers 403 to, which reads as a broken
   * page rather than a closed door.
   *
   * Set it wherever the server side is always-enforced, and keep the two in step.
   */
  strict?: boolean;
}

/**
 * Route/view guard. Wrap a confidential view so only users whose resolved RBAC
 * permissions include `permission` can see it. While permissions are loading a
 * spinner is shown; enforcement itself is decided server-side (this component
 * only hides UI — every proxy route must also authorize the same permission).
 *
 * Example:
 *   <RequirePermission permission="space.people.absence_insights.view">
 *     <AbsenceInsightsDashboard />
 *   </RequirePermission>
 */
export function RequirePermission({
  permission,
  children,
  fallback,
  strict = false,
}: RequirePermissionProps) {
  const { loading, hasPermission, permissions, isCoreAdmin } = useRBAC();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner size="lg" aria-label="Checking access" />
      </div>
    );
  }

  // Same predicate the Sidebar uses to hide a gated nav item, so a hidden item
  // and a blocked route can never disagree about who is allowed in.
  const required = typeof permission === 'string' ? [permission] : permission;
  const allowed = strict
    ? isCoreAdmin || permissions.has('*') || required.some((p) => permissions.has(p))
    : required.some((p) => hasPermission(p));

  if (!allowed) {
    return (
      fallback ?? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <h2 className="text-lg font-semibold text-zinc-900">Access restricted</h2>
          <p className="mt-1 max-w-md text-sm text-zinc-500">
            You don&apos;t have access to this page. If you believe you should, ask a People Space
            admin to add you to the relevant Google group.
          </p>
        </div>
      )
    );
  }

  return <>{children}</>;
}
